import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";

import * as React from "react";

import { Checkbox } from "@replo/design-system/components/checkbox/Checkbox";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from "@replo/design-system/components/field/Field";
import { RadioGroupItem } from "@replo/design-system/components/radio-group/RadioGroup";
import { Switch } from "@replo/design-system/components/switch/Switch";
import twMerge from "@replo/design-system/utils/twMerge";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

interface SelectionItemBaseProps<ClassName extends string = string> {
  id: string;
  label: string;
  variant?: "default" | "outline";
  description?: string;
  className?: LayoutClassName<ClassName>;
  disabled?: boolean;
  UNSAFE_className?: string;
}

type SelectionItemProps<ClassName extends string = string> =
  SelectionItemBaseProps<ClassName> &
    (
      | {
          type: "checkbox" | "switch";
          checked?: boolean;
          onCheckedChange?: (checked: boolean) => void;
        }
      | {
          type: "radio";
        }
    );

function SelectionItem<ClassName extends string>({
  type,
  id,
  label,
  variant = "default",
  description,
  className,
  disabled,
  UNSAFE_className,
  ...props
}: SelectionItemProps<ClassName>) {
  const component = exhaustiveSwitch({ type })({
    checkbox: () => (
      <Checkbox id={id} name={id} disabled={disabled} {...props} />
    ),
    switch: () => (
      <Switch id={id} name={id} size="sm" disabled={disabled} {...props} />
    ),
    radio: () => <RadioGroupItem value={id} id={id} disabled={disabled} />,
  });

  return (
    <FieldLabel
      htmlFor={id}
      UNSAFE_className={twMerge(
        variant === "outline" &&
          "cursor-pointer border border-border rounded-lg p-3 hover:bg-sheet has-data-checked:border-component-border has-data-checked:bg-sheet w-full!",
        disabled && "opacity-50 cursor-not-allowed",
        className,
        UNSAFE_className,
      )}
    >
      <Field
        orientation="horizontal"
        className={twMerge(
          "gap-3",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        {type !== "switch" && component}
        <FieldContent>
          <FieldTitle>{label}</FieldTitle>
          {description && <FieldDescription>{description}</FieldDescription>}
        </FieldContent>
        {type === "switch" && component}
      </Field>
    </FieldLabel>
  );
}

export { SelectionItem };
